import React from 'react';
import { argsToStringifiable } from '@local/finsera-core/src/helpers/embed/utils';
import { _, mui } from '@local/finsera-core/src/libs';
import * as types from '@local/finsera-core/src/types';

import type { ChartRef } from './chart';
import Chart from './chart';
import { getColor } from '../../../helpers';
import { useUiStyles } from '../../../hooks';
import CenteredLoader from '../../centered-loader';
import ChartError from '../../chart-error';
import BaseLegend from '../shared/base-legend';

type LayeredChartProps = {
  id: string;
  data: types.widgets.TableData;
  fullScreen: boolean;
  readOnly: boolean;

  xAxisField: string;
  innerBar: string;
  innerBarTitle: string;
  outerBar: string;
  outerBarTitle: string;
  line: string;
  lineTitle: string;

  leftTitle?: string;
  rightTitle?: string;

  alert?: {
    type: 'error' | 'warning';
    message: string;
  };

  download?: boolean;
  exportTitle?: string;
  exportSubTitle?: string;
  setExternalEmbed?: (_json: types.analysis.EmbedChartData) => void;
  customDownloadFormatting?: (_data: types.widgets.TableData) => types.widgets.TableData;
};

/**
 * Layered chart component that displays data with multiple visualization layers
 *
 * @remarks
 * This chart combines inner and outer bar series with a line series.
 * If xAxisField is set to 'date' (default), the values will be treated as dates
 * and the chart will behave as a time series chart. This enables additional features
 * like period selection.
 *
 * @param id - Unique identifier for the chart
 * @param data - Data to be displayed in the chart
 * @param fullScreen - Whether the chart is in fullscreen mode
 * @param readOnly - Whether the chart is in read-only mode
 * @param xAxisField - Field to use for x-axis values
 * @param innerBar - Field name for inner bar data
 * @param innerBarTitle - Title for inner bar series
 * @param outerBar - Field name for outer bar data
 * @param outerBarTitle - Title for outer bar series
 * @param line - Field name for line data
 * @param lineTitle - Title for line series
 * @param leftTitle - Title for left y-axis
 * @param rightTitle - Title for right y-axis
 * @param alert - Alert message to display above the chart
 * @param download - Whether to show download button
 * @param exportTitle - Title used when exporting the chart
 * @param exportSubTitle - Subtitle used when exporting the chart
 * @param setExternalEmbed - Callback to set external embed data
 * @param customDownloadFormatting - Custom formatting function for download data
 * @returns React component displaying a layered chart with bars and line
 */
const LayeredChart = (args: LayeredChartProps) => {
  const {
    id,
    data,
    fullScreen,
    readOnly,

    leftTitle = undefined,
    rightTitle = undefined,

    xAxisField,
    innerBar,
    innerBarTitle,
    outerBar,
    outerBarTitle,
    line,
    lineTitle,

    alert,
    download = true,
    exportTitle,
    exportSubTitle,
    setExternalEmbed,
    customDownloadFormatting = undefined,
  } = args;

  const uiStyles = useUiStyles();
  const [localData, setLocalData] = React.useState<types.widgets.TableData>(null);
  const chartRef = React.useRef<ChartRef>(null);
  const [hiddenSeries, setHiddenSeries] = React.useState<Set<string>>(new Set());

  const seriesKeys = [innerBarTitle, outerBarTitle, lineTitle];
  const seriesColorByName = React.useMemo(() => {
    const barColor = getColor(1);
    return {
      [innerBarTitle]: barColor,
      [outerBarTitle]: barColor,
      [lineTitle]: '#a706d4',
    };
  }, [innerBarTitle, outerBarTitle, lineTitle]);

  const shouldShowLegend = !_.isEmpty(seriesKeys) && (fullScreen || seriesKeys.length <= 6);

  React.useEffect(() => {
    const handleData = async () => {
      setLocalData(null);
      if (_.isNil(data)) return;
      await new Promise((resolve) => setTimeout(resolve, 200));

      if (setExternalEmbed)
        setExternalEmbed({
          id,
          widgetTitle: exportTitle,
          widgetSubTitle: exportSubTitle,
          chart_type: 'layered-chart-v5',
          args: argsToStringifiable({
            ...args,
            data,
          }),
        });

      setLocalData(data);
    };

    handleData();
  }, [data]);

  return (
    <mui.core.Box
      sx={{
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        display: 'grid',
        gridTemplateRows: `${alert ? 'auto ' : ''} 1fr`,
      }}
    >
      {alert && <ChartError readOnly={readOnly} error={alert.message} severity={alert.type} />}

      <mui.core.Box sx={uiStyles.widgetContent}>
        <mui.core.Box
          sx={{
            height: fullScreen ? 'calc(100% - 40px)' : '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {_.isNil(localData) ? (
            <CenteredLoader label="Loading widget data..." top="-40px" />
          ) : (
            <>
              <mui.core.Box sx={{ flex: 1, minHeight: 0 }}>
                <Chart
                  ref={chartRef}
                  id={id}
                  data={localData}
                  fullScreen={fullScreen}
                  xAxisField={xAxisField}
                  download={download}
                  leftTitle={leftTitle}
                  rightTitle={rightTitle}
                  customDownloadFormatting={customDownloadFormatting}
                  innerBar={innerBar}
                  innerBarTitle={innerBarTitle}
                  outerBar={outerBar}
                  outerBarTitle={outerBarTitle}
                  line={line}
                  lineTitle={lineTitle}
                  seriesColorByName={seriesColorByName}
                />
              </mui.core.Box>
              {shouldShowLegend && !_.isNil(localData) && (
                <mui.core.Box sx={{ flexShrink: 0 }}>
                  <BaseLegend
                    seriesColorByName={seriesColorByName}
                    seriesKeys={seriesKeys}
                    chartRef={chartRef}
                    hiddenSeries={hiddenSeries}
                    setHiddenSeries={setHiddenSeries}
                  />
                </mui.core.Box>
              )}
            </>
          )}
        </mui.core.Box>
      </mui.core.Box>
    </mui.core.Box>
  );
};

export default LayeredChart;
